import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth';
import { AppSettingsService } from '@core/services/app-settings.service';
import { PreferredOrganizationService } from '@core/services/preferred-organization.service';
import { SystemSettingsService } from '@core/services/system-settings.service';
import { getEntityReadiness } from '@core/models/entity-readiness';
import { ModuleVersion } from 'src/app/system/models/system-settings';

/**
 * Blocks pages whose core depends on the offline-first sync cycle outside the
 * preferred organization (US 27702): Order 3.0 (every `posVersion: V3_0` route),
 * cash in/out, register open/close and closures, and the offline v2.5 sales
 * orders list. A new Order 2.5 is blocked as well while the organization's
 * "Save order offline first" setting applies to the current user (Bug 28417):
 * that order would be written locally, which only the preferred organization
 * does. Pages that merely read IndexedDB-first (Fixed Assets, Manufacturing,
 * lookups) are NOT routed here — they read from the server instead
 * (DataSourceService).
 *
 * The route declares `data.entityKey`; a route carrying `data.posVersion === V3_0`
 * counts as 'posV3' without an explicit key.
 */
export const entityReadinessGuard: CanActivateFn = async (route) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const preferredOrganizationService = inject(PreferredOrganizationService);
    const systemSettingsService = inject(SystemSettingsService);

    const entityKey: string | undefined = route.data['entityKey']
        ?? (route.data['posVersion'] === ModuleVersion.V3_0 ? 'posV3' : undefined);
    const readiness = getEntityReadiness(entityKey);
    if (readiness === 'both') return true;

    const preferred = preferredOrganizationService.preferred;
    // Without a preferred organization there is nothing to compare against; the
    // preferredOrganizationGuard on the parent route owns that situation.
    if (!preferred) return true;

    const onPreferred = preferredOrganizationService.isPreferredTenant(authService.currentTenant);
    let blocked = false;
    if (readiness === 'local-only') {
        blocked = !onPreferred;
    } else if (readiness === 'server-ready') {
        blocked = onPreferred;
    } else if (!onPreferred) {
        // 'local-only-when-offline-first': the organization's setting is read only when
        // it can block, so the preferred organization never waits on it here.
        blocked = await isOfflineFirstUser(systemSettingsService, authService);
    }
    if (!blocked) return true;

    return router.createUrlTree(['/blocked'], { queryParams: { entity: entityKey } });
};

/**
 * The v2.5 order form's own offline-first decision (its `offlineModeEnabled`
 * getter), minus the preferred-organization part this guard already resolved.
 */
async function isOfflineFirstUser(systemSettingsService: SystemSettingsService, authService: AuthService): Promise<boolean> {
    if (!AppSettingsService.appSettings.offlineMode.enabled) return false;
    return systemSettingsService.isOfflineFirstUser(authService.currentUserEmail);
}
